import { StoredCheckin, Tone, toneOf } from "../lib/checkin";

const WINDOW_DAYS = 28;
const ROWS = [
  { key: "sleep", label: "Sleep" },
  { key: "feel", label: "Feeling" },
  { key: "pain", label: "Pain" },
] as const;
const TONE_WORDS: Record<Tone, string> = { good: "good", "so-so": "so-so", poor: "poor or hurting" };

/**
 * The last four weeks of check-ins, one square per workout, for the coach.
 *
 * One check-in says little; a run of them says a lot. "Not feeling great" three sessions running is
 * the pattern a coach wants to catch, and it's invisible one session row at a time. Only that pattern
 * is called out in words. The rest is left to the coach to read.
 */
export function CheckinTrend({
  sessions,
  now = Date.now(),
}: {
  sessions: { completed_at: string; checkin?: StoredCheckin | null }[];
  now?: number;
}) {
  const since = now - WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const checkins = sessions
    .filter((session) => session.checkin && Date.parse(session.completed_at) >= since)
    .sort((a, b) => a.completed_at.localeCompare(b.completed_at));
  if (checkins.length === 0) return null;

  // Newest last above, so count back from the end.
  let roughRun = 0;
  for (let i = checkins.length - 1; i >= 0 && checkins[i].checkin!.feel === "rough"; i--) roughRun++;

  return (
    <section className="checkin-trend" aria-labelledby="checkin-trend-heading">
      <h3 id="checkin-trend-heading" className="eyebrow">
        Check-ins · last 4 weeks
      </h3>
      {ROWS.map((row) => (
        <div key={row.key} className="checkin-trend-row">
          <span className="checkin-trend-label">{row.label}</span>
          <ol className="checkin-trend-cells" aria-label={`${row.label}, oldest first`}>
            {checkins.map((session) => {
              const tone = toneOf(row.key, session.checkin!);
              const day = new Date(session.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
              return (
                <li key={session.completed_at} className={`checkin-cell tone-${tone}`} title={`${day}: ${TONE_WORDS[tone]}`}>
                  <span className="visually-hidden">{`${day}: ${TONE_WORDS[tone]}`}</span>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
      <p className="checkin-trend-legend">
        <span className="checkin-cell tone-good" aria-hidden="true" /> Good
        <span className="checkin-cell tone-so-so" aria-hidden="true" /> So-so
        <span className="checkin-cell tone-poor" aria-hidden="true" /> Poor / hurting
      </p>
      {roughRun >= 3 && <p className="form-hint">Not feeling great {roughRun} sessions running.</p>}
    </section>
  );
}
